/**
 * PATH: packages/ui/src/components/notification-center/NotificationRow.tsx
 * FILE_ID: UI-NOTIFCENTER-ROW-001
 * PURPOSE: One notification row with severity, actions, and a deep link.
 * WHY: Read rows used slate-900, which painted the list the same blue as the shell.
 */

import { motion } from 'framer-motion'
import { AtSign, Check, X, ExternalLink } from 'lucide-react'
import { cn } from '../../lib/utils'
import { formatDistanceToNow } from 'date-fns'
import type { NotificationItem } from './types'
import { getSeverityStyles } from './types'

interface NotificationRowProps {
  notification: NotificationItem
  onMarkAsRead?: (id: string) => void
  onDismiss?: (id: string) => void
  onNavigate?: (url: string) => void
}

export function NotificationRow({ notification, onMarkAsRead, onDismiss, onNavigate }: NotificationRowProps) {
  const styles = getSeverityStyles(notification.severity)
  const SeverityIcon = styles.icon
  const isMention = notification.category === 'mentions' || notification.type === 'mention'
  const isUnread = !notification.read_at && !notification.is_read
  const nid = String(notification.id)

  const displayTitle = notification.title && notification.title !== 'Notification'
    ? notification.title
    : notification.message
  const displayMessage = notification.title && notification.title !== 'Notification'
    ? notification.message
    : undefined

  const navUrl = notification.action_url
    || notification.entity_link
    || (notification.job_number ? `/jobs/${notification.job_number}` : null)

  const handleClick = () => {
    if (navUrl) {
      if (isUnread && onMarkAsRead) onMarkAsRead(nid)
      if (onNavigate) {
        onNavigate(navUrl)
      } else if (navUrl.startsWith('/')) {
        window.location.href = navUrl
      } else {
        window.open(navUrl, '_blank')
      }
    }
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -100 }}
      onClick={handleClick}
      className={cn(
        'relative flex gap-3 p-3 rounded-xl border transition-all duration-200 group',
        isUnread
          ? `${styles.bgColor} ${styles.borderColor}`
          : 'bg-card border-border opacity-75',
        'hover:shadow-sm',
        navUrl && 'cursor-pointer'
      )}
    >
      {isUnread && (
        <div className={cn('absolute top-3 left-0 w-1 h-6 rounded-r-full', styles.dotColor)} />
      )}

      <div
        className={cn(
          'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center',
          isUnread ? styles.textColor : 'bg-muted text-muted-foreground'
        )}
      >
        {isMention ? <AtSign className="w-5 h-5" /> : <SeverityIcon className="w-5 h-5" />}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p
              className={cn(
                'text-sm font-medium truncate',
                isUnread ? 'text-foreground' : 'text-muted-foreground'
              )}
              title={typeof displayTitle === 'string' ? displayTitle : undefined}
            >
              {displayTitle}
            </p>
            {displayMessage && (
              <p
                className="text-xs text-muted-foreground mt-0.5 line-clamp-2"
                title={typeof displayMessage === 'string' ? displayMessage : undefined}
              >
                {displayMessage}
              </p>
            )}
          </div>

          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {isUnread && onMarkAsRead && (
              <button
                onClick={(e) => { e.stopPropagation(); onMarkAsRead(nid) }}
                className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                title="Mark as read"
              >
                <Check className="w-4 h-4" />
              </button>
            )}
            {onDismiss && (
              <button
                onClick={(e) => { e.stopPropagation(); onDismiss(nid) }}
                className="p-1.5 rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {notification.sender_name && (
              <span className="flex items-center gap-1">
                <AtSign className="w-3 h-3" />
                {notification.sender_name}
              </span>
            )}
            {notification.job_number && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-muted rounded text-muted-foreground">
                Job {notification.job_number}
              </span>
            )}
            <span>{notification.time_ago || formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}</span>
          </div>

          {navUrl && (
            <span className="flex items-center gap-1 text-xs text-primary font-medium">
              {notification.action_label || 'View'}
              <ExternalLink className="w-3 h-3" />
            </span>
          )}
        </div>
      </div>
    </motion.div>
  )
}
